import { useEffect, useState } from 'react';
import type { AvoidSchedule, BuildSchedule, HabitCategory, HabitPlan, HabitView, ObjectiveSummaryView } from '@contracts';
import { useDomainApi, useDomainVersion } from '@ui/shared/domain/DomainApiContext';
import { ChevronLeft, TrendingUp, Shield } from '@ui/shared/icons';
import { SkeletonBlock } from '@ui/shared/primitives/Skeleton';
import { HABIT_CATEGORIES, habitCategoryMeta } from '@ui/shared/habits/habitCategory';
import { Field, PrimaryButton, SecondaryButton, inputStyle } from '@ui/shared/primitives/FormControls';
import { FooterTextButton, LifecycleConfirmDialog, LifecycleFooterShell } from '@ui/shared/primitives/LifecycleFooter';
import { ObjectivePickerField } from './ObjectivePickerField';

/**
 * Habit create/edit screen (phase-2b-habits.md HB-17): name, category, type
 * (build/avoid — fixed for life, HB-1) with guidance copy shown BEFORE
 * either type is picked (AC-B3), frequency (build: daily or N×/week;
 * avoid: at-most-N×/week, N>=0 — "Zero — aiming for none this week" at
 * N=0 per desktop-manage.md SS1.2's ratified copy), and pause/resume/
 * archive footer actions on the edit path (HB-3). Every field persists on
 * commit — no save button for edits, matching every other Compass form.
 * The linked-objective field (habit-form.md §1.5, phase-3-plan.md HL-1)
 * is the form's last content field before the footer buttons: a single-
 * select "None" + active-objectives-only picker, wired to `objectiveId` on
 * both the create draft and the edit-path commit.
 */
export function HabitFormScreen({
  habitId,
  onDone,
  onCancel,
}: {
  /** Null = create flow; a HabitId string = edit flow. */
  readonly habitId: string | null;
  readonly onDone: () => void;
  readonly onCancel: () => void;
}) {
  const api = useDomainApi();
  const version = useDomainVersion();
  const isEdit = habitId !== null;

  const [loaded, setLoaded] = useState(!isEdit);
  const [existing, setExisting] = useState<HabitView | null>(null);
  const [activeObjectives, setActiveObjectives] = useState<readonly ObjectiveSummaryView[]>([]);
  // habit-form.md §2 "Edit path, habit already linked": if the linked
  // objective has since been archived, the closed field must still show its
  // title (an archived objectiveId is still a valid, resolvable reference) —
  // this separate, includeArchived:true list is ONLY used to resolve that
  // display label, never as the picker's own offered options (those stay
  // active-only, per AC-P1, via activeObjectives above).
  const [allObjectivesForLabel, setAllObjectivesForLabel] = useState<readonly ObjectiveSummaryView[]>([]);

  const [name, setName] = useState('');
  const [nameTouched, setNameTouched] = useState(false);
  const [category, setCategory] = useState<HabitCategory>('health');
  const [habitKind, setHabitKind] = useState<'build' | 'avoid' | null>(null);
  const [buildFrequency, setBuildFrequency] = useState<'daily' | 'times_per_week'>('daily');
  const [buildTarget, setBuildTarget] = useState(3);
  const [avoidLimit, setAvoidLimit] = useState(2);
  const [objectiveId, setObjectiveId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // habit-form.md §1.5: the picker's OWN options are always active-only
  // (AC-P1), independent of the edit/create branch below — a create flow
  // needs this list just as much as an edit flow does.
  useEffect(() => {
    void api.listObjectives().then(setActiveObjectives);
    void api.listObjectives({ includeArchived: true }).then(setAllObjectivesForLabel);
  }, [api, version]);

  useEffect(() => {
    if (!isEdit || !habitId) return;
    let cancelled = false;
    void api.listHabits({ includeArchived: true }).then((list) => {
      if (cancelled) return;
      const found = list.find((h) => h.habitId === habitId) ?? null;
      setExisting(found);
      if (found) {
        setName(found.name);
        setCategory(found.category);
        setHabitKind(found.plan.habitKind);
        setObjectiveId(found.objectiveId);
        if (found.plan.habitKind === 'build') {
          setBuildFrequency(found.plan.schedule.kind);
          if (found.plan.schedule.kind === 'times_per_week') {
            setBuildTarget(found.plan.schedule.targetPerWeek);
          }
        } else {
          setAvoidLimit(found.plan.schedule.limitPerWeek);
        }
      }
      setLoaded(true);
    });
    return () => {
      cancelled = true;
    };
  }, [api, habitId, isEdit, version]);

  if (isEdit && !loaded) {
    return (
      <div style={{ padding: 'var(--space-6) var(--layout-page-margin-phone) 0' }}>
        <SkeletonBlock height={20} width={80} />
        <div style={{ height: 'var(--space-4)' }} />
        <SkeletonBlock height={32} />
      </div>
    );
  }

  if (isEdit && !existing) {
    return (
      <main style={{ padding: 'var(--space-6) var(--layout-page-margin-phone)' }}>
        <p style={{ color: 'var(--text-secondary)' }}>Habit not found.</p>
        <div style={{ marginTop: 'var(--space-4)' }}>
          <SecondaryButton onClick={onCancel}>Back</SecondaryButton>
        </div>
      </main>
    );
  }

  const canSubmit = name.trim().length > 0 && habitKind !== null && !submitting;

  function planFromForm(): HabitPlan {
    if (habitKind === 'avoid') {
      const schedule: AvoidSchedule = { kind: 'weekly_limit', limitPerWeek: avoidLimit };
      return { habitKind: 'avoid', schedule };
    }
    const schedule: BuildSchedule =
      buildFrequency === 'daily' ? { kind: 'daily' } : { kind: 'times_per_week', targetPerWeek: buildTarget };
    return { habitKind: 'build', schedule };
  }

  async function submit() {
    setNameTouched(true);
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      if (isEdit && habitId) {
        await api.editHabit(habitId as never, { name: name.trim(), category, objectiveId: objectiveId as never });
        // Frequency changes on an existing habit are a manual target
        // adjustment (HB-4), kind-matched and distinct from the cosmetic
        // habit.edited path above — mirrors RW-3's manual-vs-suggested
        // asymmetry (no realism bonus for a self-initiated edit).
        if (existing) {
          const nextPlan = planFromForm();
          if (nextPlan.habitKind === existing.plan.habitKind && JSON.stringify(nextPlan) !== JSON.stringify(existing.plan)) {
            if (nextPlan.habitKind === 'build' && existing.plan.habitKind === 'build') {
              await api.adjustTargetManually(habitId as never, {
                habitKind: 'build',
                previous: existing.plan.schedule,
                next: nextPlan.schedule,
              });
            } else if (nextPlan.habitKind === 'avoid' && existing.plan.habitKind === 'avoid') {
              await api.adjustTargetManually(habitId as never, {
                habitKind: 'avoid',
                previous: existing.plan.schedule,
                next: nextPlan.schedule,
              });
            }
          }
        }
      } else {
        await api.createHabit({
          name: name.trim(),
          category,
          plan: planFromForm(),
          objectiveId: objectiveId as never,
        });
      }
      onDone();
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main
      aria-label={isEdit ? `Edit habit: ${existing?.name ?? ''}` : 'New habit'}
      style={{ padding: 'var(--space-6) var(--layout-page-margin-phone) var(--space-8)', maxWidth: '480px', margin: '0 auto' }}
    >
      <button
        type="button"
        onClick={onCancel}
        aria-label="Back"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-1)',
          color: 'var(--text-muted)',
          fontSize: 'var(--type-caption-size)',
          fontWeight: 600,
          minHeight: 'var(--touch-target-min)',
          marginLeft: '-8px',
        }}
      >
        <ChevronLeft size={16} aria-hidden="true" />
        Back
      </button>

      <h1
        style={{
          fontSize: 'var(--type-heading-size)',
          fontWeight: 'var(--type-heading-weight)',
          color: 'var(--text-primary)',
          marginTop: 'var(--space-3)',
        }}
      >
        {isEdit ? 'Edit habit' : 'New habit'}
      </h1>

      <Field label="Name">
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          onBlur={() => setNameTouched(true)}
          placeholder="e.g. Gym"
          style={inputStyle}
        />
      </Field>
      {nameTouched && name.trim().length === 0 && (
        <p role="status" style={{ color: 'var(--text-secondary)', fontSize: 'var(--type-caption-size)', marginTop: 'var(--space-2)' }}>
          Give your habit a name to continue.
        </p>
      )}

      <div style={{ marginTop: 'var(--space-4)' }}>
        <span style={{ display: 'block', fontSize: 'var(--type-caption-size)', color: 'var(--text-muted)', marginBottom: 'var(--space-2)' }}>
          Category
        </span>
        <div role="radiogroup" aria-label="Category" style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)' }}>
          {HABIT_CATEGORIES.map((cat) => {
            const meta = habitCategoryMeta(cat);
            const selected = cat === category;
            return (
              <button
                key={cat}
                type="button"
                role="radio"
                aria-checked={selected}
                onClick={() => setCategory(cat)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-1)',
                  padding: '0 var(--space-3)',
                  minHeight: 'var(--touch-target-min)',
                  borderRadius: 'var(--radius-md)',
                  border: selected ? `1px solid var(--accent-${meta.tokenSuffix})` : '1px solid var(--border-strong)',
                  background: selected ? `color-mix(in srgb, var(--accent-${meta.tokenSuffix}-vivid) 14%, var(--surface-raised))` : 'transparent',
                  color: selected ? 'var(--text-primary)' : 'var(--text-secondary)',
                  fontSize: 'var(--type-body-size)',
                  fontWeight: 500,
                }}
              >
                <meta.Icon size={16} color={`var(--accent-${meta.tokenSuffix})`} />
                {meta.label}
              </button>
            );
          })}
        </div>
      </div>

      <div style={{ marginTop: 'var(--space-4)' }}>
        <span style={{ display: 'block', fontSize: 'var(--type-caption-size)', color: 'var(--text-muted)', marginBottom: 'var(--space-2)' }}>
          Type
        </span>
        <div role="radiogroup" aria-label="Type" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
          <TypeOptionCard
            icon={<TrendingUp size={20} aria-hidden="true" />}
            title="Build"
            description="Something you want to do. Exercise, read, practice."
            frequency="Daily, or at least N times a week."
            selected={habitKind === 'build'}
            disabled={isEdit}
            onSelect={() => setHabitKind('build')}
          />
          <TypeOptionCard
            icon={<Shield size={20} aria-hidden="true" />}
            title="Avoid"
            description="Something you want to limit. Alcohol, sweets, doom-scrolling."
            frequency="A weekly allowance: at most N times."
            selected={habitKind === 'avoid'}
            disabled={isEdit}
            onSelect={() => setHabitKind('avoid')}
          />
        </div>
        <p style={{ fontSize: 'var(--type-caption-size)', color: 'var(--text-secondary)', marginTop: 'var(--space-2)' }}>
          Rule of thumb: if it&apos;s about doing a good thing, build it — &quot;exercise 3× a week&quot;, not &quot;avoid skipping
          exercise&quot;.
        </p>
      </div>

      {habitKind === 'build' && (
        <div style={{ marginTop: 'var(--space-4)' }}>
          <span style={{ display: 'block', fontSize: 'var(--type-caption-size)', color: 'var(--text-muted)', marginBottom: 'var(--space-2)' }}>
            Frequency
          </span>
          <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}>
            <FrequencySegment
              label="Daily"
              selected={buildFrequency === 'daily'}
              onSelect={() => setBuildFrequency('daily')}
            />
            <FrequencySegment
              label="N× a week"
              selected={buildFrequency === 'times_per_week'}
              onSelect={() => setBuildFrequency('times_per_week')}
            />
            {buildFrequency === 'times_per_week' && (
              <NumberStepper value={buildTarget} min={1} max={7} onChange={setBuildTarget} />
            )}
          </div>
        </div>
      )}

      {habitKind === 'avoid' && (
        <div style={{ marginTop: 'var(--space-4)' }}>
          <span style={{ display: 'block', fontSize: 'var(--type-caption-size)', color: 'var(--text-muted)', marginBottom: 'var(--space-2)' }}>
            At most, per week
          </span>
          <NumberStepper value={avoidLimit} min={0} max={14} onChange={setAvoidLimit} />
          <p style={{ fontSize: 'var(--type-caption-size)', color: 'var(--text-secondary)', marginTop: 'var(--space-2)' }}>
            {avoidLimit === 0 ? 'Zero — aiming for none this week.' : `Up to ${avoidLimit} times allowed this week.`}
          </p>
        </div>
      )}

      {/* habit-form.md §1.5: the form's last content field before the
          footer buttons, for both build and avoid habits. The edit path's
          "already linked to an objective no longer in the active list"
          case (§2 States) resolves the closed-field label against the
          existing habit's own known objectiveId even when it falls outside
          `activeObjectives` — see resolvedObjectiveTitle below. */}
      <div style={{ marginTop: 'var(--space-4)' }}>
        <span style={{ display: 'block', fontSize: 'var(--type-caption-size)', color: 'var(--text-muted)', marginBottom: 'var(--space-1)' }}>
          Linked objective
        </span>
        <ObjectivePickerField
          value={objectiveId}
          activeObjectives={activeObjectives}
          linkedTitleFallback={
            allObjectivesForLabel.find((o) => o.objectiveId === objectiveId)?.title ?? null
          }
          onChange={setObjectiveId}
        />
      </div>

      <div style={{ display: 'flex', gap: 'var(--space-3)', marginTop: 'var(--space-6)' }}>
        <PrimaryButton onClick={() => void submit()} disabled={!canSubmit}>
          {isEdit ? 'Save changes' : 'Create habit'}
        </PrimaryButton>
        <SecondaryButton onClick={onCancel}>Cancel</SecondaryButton>
      </div>

      {isEdit && existing && habitId && (
        <HabitLifecycleFooter habitId={habitId} state={existing.state} name={existing.name} onChanged={onDone} />
      )}
    </main>
  );
}

function TypeOptionCard({
  icon,
  title,
  description,
  frequency,
  selected,
  disabled,
  onSelect,
}: {
  readonly icon: React.ReactNode;
  readonly title: string;
  readonly description: string;
  readonly frequency: string;
  readonly selected: boolean;
  readonly disabled: boolean;
  readonly onSelect: () => void;
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      disabled={disabled}
      onClick={onSelect}
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 'var(--space-3)',
        padding: 'var(--space-4)',
        borderRadius: 'var(--radius-lg)',
        border: selected ? '1px solid var(--hue-primary)' : '1px solid var(--border-subtle)',
        background: selected ? 'color-mix(in srgb, var(--hue-primary-vivid) 12%, var(--surface-raised))' : 'var(--surface-raised)',
        textAlign: 'left',
        opacity: disabled && !selected ? 0.5 : 1,
      }}
    >
      <span style={{ color: 'var(--text-secondary)', flex: 'none' }}>{icon}</span>
      <span>
        <span style={{ display: 'block', fontSize: 'var(--type-body-size)', fontWeight: 600, color: 'var(--text-primary)' }}>
          {title}
        </span>
        <span style={{ display: 'block', fontSize: 'var(--type-caption-size)', color: 'var(--text-secondary)', marginTop: '2px' }}>
          {description}
        </span>
        <span style={{ display: 'block', fontSize: 'var(--type-caption-size)', color: 'var(--text-muted)', marginTop: '2px' }}>
          {frequency}
        </span>
      </span>
    </button>
  );
}

function FrequencySegment({
  label,
  selected,
  onSelect,
}: {
  readonly label: string;
  readonly selected: boolean;
  readonly onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      style={{
        padding: '0 var(--space-3)',
        minHeight: 'var(--touch-target-min)',
        borderRadius: 'var(--radius-md)',
        border: selected ? '1px solid var(--hue-primary)' : '1px solid var(--border-strong)',
        background: selected ? 'var(--surface-hover)' : 'transparent',
        color: selected ? 'var(--text-primary)' : 'var(--text-secondary)',
        fontWeight: 600,
        fontSize: 'var(--type-body-size)',
      }}
    >
      {label}
    </button>
  );
}

function NumberStepper({
  value,
  min,
  max,
  onChange,
}: {
  readonly value: number;
  readonly min: number;
  readonly max: number;
  readonly onChange: (next: number) => void;
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
      <button
        type="button"
        onClick={() => onChange(Math.max(min, value - 1))}
        aria-label="Decrease"
        disabled={value <= min}
        style={{
          width: 'var(--touch-target-min)',
          height: 'var(--touch-target-min)',
          borderRadius: 'var(--radius-md)',
          border: '1px solid var(--border-strong)',
          color: value <= min ? 'var(--text-disabled)' : 'var(--text-secondary)',
          fontSize: 'var(--type-body-size)',
          fontWeight: 600,
        }}
      >
        −
      </button>
      <span className="num" style={{ minWidth: '24px', textAlign: 'center', fontSize: 'var(--type-body-size)', fontWeight: 600, color: 'var(--text-primary)' }}>
        {value}
      </span>
      <button
        type="button"
        onClick={() => onChange(Math.min(max, value + 1))}
        aria-label="Increase"
        disabled={value >= max}
        style={{
          width: 'var(--touch-target-min)',
          height: 'var(--touch-target-min)',
          borderRadius: 'var(--radius-md)',
          border: '1px solid var(--border-strong)',
          color: value >= max ? 'var(--text-disabled)' : 'var(--text-secondary)',
          fontSize: 'var(--type-body-size)',
          fontWeight: 600,
        }}
      >
        +
      </button>
    </div>
  );
}

/**
 * Pause/resume/archive footer (HB-3, desktop-manage.md SS1.2's supportive
 * copy pattern). No delete exists anywhere — absent, not hidden (AC-B4).
 * Shares its chrome (text action, inline `alertdialog` confirmation) with
 * `ObjectiveLifecycleFooter.tsx` via `@ui/shared/primitives/LifecycleFooter`.
 */
function HabitLifecycleFooter({
  habitId,
  state,
  name,
  onChanged,
}: {
  readonly habitId: string;
  readonly state: HabitView['state'];
  readonly name: string;
  readonly onChanged: () => void;
}) {
  const api = useDomainApi();
  const [confirming, setConfirming] = useState<'pause' | 'archive' | null>(null);

  return (
    <LifecycleFooterShell>
      {state === 'paused' && (
        <p style={{ fontSize: 'var(--type-caption-size)', color: 'var(--text-secondary)', marginBottom: 'var(--space-3)' }}>
          Paused.
        </p>
      )}
      <div style={{ display: 'flex', gap: 'var(--space-4)', flexWrap: 'wrap' }}>
        {state === 'active' && (
          <FooterTextButton
            onClick={() => setConfirming('pause')}
            label="Pause"
          />
        )}
        {state === 'paused' && (
          <FooterTextButton
            onClick={() => {
              void api.resumeHabit(habitId as never).then(onChanged);
            }}
            label="Resume"
          />
        )}
        {state !== 'archived' && (
          <FooterTextButton onClick={() => setConfirming('archive')} label="Archive" />
        )}
      </div>
      <p style={{ fontSize: 'var(--type-caption-size)', color: 'var(--text-muted)', marginTop: 'var(--space-2)' }}>
        Habits archive rather than delete, so your history stays honest.
      </p>

      {confirming && (
        <LifecycleConfirmDialog
          ariaLabel={confirming === 'pause' ? `Pause ${name}` : `Archive ${name}`}
          message={
            confirming === 'pause'
              ? `Pause "${name}"? It leaves the daily page and the weekly math until you resume. History stays.`
              : `Archive "${name}"? It moves out of active views. Every event stays in your history and your export.`
          }
          confirmLabel={confirming === 'pause' ? 'Pause' : 'Archive'}
          onConfirm={() => {
            const action = confirming === 'pause' ? api.pauseHabit(habitId as never) : api.archiveHabit(habitId as never);
            void action.then(() => {
              setConfirming(null);
              onChanged();
            });
          }}
          onCancel={() => setConfirming(null)}
        />
      )}
    </LifecycleFooterShell>
  );
}
